import { FoodsIcon, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { BrandAccessSummary } from "./brandAccess";

export const BrandAccessLabel = (props: { summary: BrandAccessSummary }) => {
    const { summary } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Typography variant="small-medium" sx={{ color: theme.custom.grey[2800] }}>
            {formatBrandAccess(summary, t)}
        </Typography>
    );
};

const formatBrandAccess = (summary: BrandAccessSummary, t: TFunction) => {
    switch (summary.kind) {
        case "owner-selection":
            return "—";
        case "all-subsidiaries":
            return t("brands.access.allSubsidiaries", "All subsidiaries");
        case "shared-by":
            return t("brands.access.sharedBy", "Shared by {{owner}}", { owner: summary.ownerName });
    }
};

type BrandRowActionsProps = {
    brandName: string;
    canModify: boolean;
    canDelete: boolean;
    onEdit: () => void;
    onDelete: () => void;
};

export const BrandRowActions = (props: BrandRowActionsProps) => {
    const { brandName, canModify, canDelete, onEdit, onDelete } = props;
    const { t } = useTranslation();

    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <BrandRowActionButton
                icon="edit"
                label={t("brands.editBrandNamed", "Edit {{name}}", { name: brandName })}
                disabled={!canModify}
                onClick={onEdit}
            />
            <BrandRowActionButton
                icon="delete"
                label={t("brands.deleteBrandNamed", "Delete {{name}}", { name: brandName })}
                disabled={!canDelete}
                onClick={onDelete}
            />
        </Stack>
    );
};

type BrandRowActionButtonProps = {
    icon: "edit" | "delete";
    label: string;
    disabled: boolean;
    onClick: () => void;
};

const BrandRowActionButton = (props: BrandRowActionButtonProps) => {
    const { icon, label, disabled, onClick } = props;
    const theme = useTheme();

    return (
        <Box
            component="button"
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            sx={{
                width: theme.spacing(4),
                height: theme.spacing(4),
                borderRadius: "50%",
                border: "none",
                backgroundColor: "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                "&:hover:not(:disabled)": { backgroundColor: theme.custom.grey[100] },
                "&:disabled": { cursor: "not-allowed", opacity: 0.4 },
            }}
        >
            <FoodsIcon icon={icon} size={1} />
        </Box>
    );
};
